'use client';

/**
 * hooks/useTurnstile.ts
 *
 * Shared Cloudflare Turnstile hook — cycle 89 (2026-05-27).
 *
 * Provides invisible/managed Turnstile challenge behaviour for all forms on the site.
 * Mirrors the integration pattern established in HelpChatClient.tsx (Cycle 15).
 *
 * Usage:
 *   const { containerRef, getToken, resetWidget } = useTurnstile();
 *
 *   // In JSX — place anywhere in the component tree; it renders as an off-screen div:
 *   <div ref={containerRef} />
 *
 *   // On form submit — get the token and include it in the POST body:
 *   const turnstileToken = getToken();
 *   await fetch('/api/subscribe', { body: JSON.stringify({ ..., turnstileToken }) });
 *
 *   // After a successful submit — reset the widget so the next submit gets a fresh token:
 *   resetWidget();
 *
 * Behaviour:
 *   - Widget loads once on mount (script tag is idempotent — shared across all form instances).
 *   - Uses `render=explicit` mode to avoid auto-scan race conditions (same fix as Cycle 84 Bug A).
 *   - Widget renders in off-screen absolute position — invisible to the user unless Cloudflare
 *     determines an interactive challenge is needed (managed/invisible mode).
 *   - Fail-open: when NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set, the hook is a no-op and
 *     getToken() returns ''. The server routes all accept an empty token in that case.
 *   - Tokens are single-use. Call resetWidget() after each successful submit.
 *
 * SINGLETON WIDGET FIX (2026-07-16 — Microsoft Clarity real-visitor error, template-wide
 * on every /blog/[slug] page):
 *   The blog post template mounts THREE newsletter-form instances simultaneously on every
 *   page load — NewsletterForm.tsx (end of article), SidebarNewsletterCard (desktop sidebar),
 *   and a second SidebarNewsletterCard (mobile drawer, hidden via CSS but still mounted in
 *   the DOM). Each instance previously called its own `useTurnstile()`, and each hook
 *   instance independently called `turnstile.render()` on mount — three concurrent widget
 *   renders against the same sitekey within the same page load. Cloudflare flagged that
 *   pattern and threw `Uncaught TurnstileError: [Cloudflare Turnstile] Error: 600010`
 *   (generic challenge failure) three times on every single blog post, reproduced 3/3 on
 *   staging + production (the-magic-of-czerny-etudes-for-the-piano, how-to-play-fur-elise,
 *   piano-technique-of-horowitz). This is what Clarity's ScriptErrorCount caught firing in
 *   the first second of a real visitor session.
 *
 *   Fix: only the FIRST hook instance to mount actually renders a widget (module-level
 *   singleton `sharedWidgetId` / `sharedContainer`). Every other instance on the same page
 *   detects the existing widget and skips rendering, but still reads/resets the shared
 *   widget via getToken()/resetWidget() — so every form's submit flow is unaffected, only
 *   ONE physical Turnstile challenge exists per page. The owning instance clears the
 *   singleton on unmount so the next page/navigation gets a fresh widget.
 *
 * Forms covered (cycle 89):
 *   - /contact          → ContactInteractive.tsx → /api/contact
 *   - Newsletter signup → useNewsletterSubmit.ts → /api/subscribe
 *   - /free-pdf-downloads → FreebieForm.tsx → /api/freebie
 *   - /help email-transcript → HelpChatClient.tsx (already wired; NOT managed by this hook)
 *   - /store, /store/distance → POST /api/subscribe (same hook, same fix below)
 *
 * ERROR-CALLBACK / RETRY FIX (2026-07-26 — SOP-OPS-10 Rule 11 outage remediation):
 *   render() previously had no `error-callback`. When Cloudflare Turnstile throws a
 *   client-side error (e.g. `[Cloudflare Turnstile] Error: 600010`), the widget was left
 *   in a dead state forever: sharedWidgetId stayed set but the challenge never solved,
 *   so getToken() returned '' permanently and every subsequent submit on that page load
 *   was silently sent with no token — the server correctly fails closed on an empty/
 *   missing token (403 `error-turnstile`), but the *visitor* had zero chance to recover
 *   without a full page reload. Confirmed as the root cause of the 2026-07-26 contact-
 *   form outage (ae-live-outcome: 10/10 real submissions failing 403 over 14 days) via
 *   this repo's scripts/e2e-form-submit.mjs, which reproduced the identical failure
 *   signature against the real sitekey while solving Cloudflare's official
 *   always-passes test sitekey without issue — i.e. the real widget was erroring and
 *   never retrying, not a secret/config/hostname defect (those were independently ruled
 *   out on 2026-07-25/26, see app/api/contact/route.ts comment above the same fix date).
 *   Fix: `error-callback` now clears the singleton and re-renders a fresh widget
 *   (bounded to MAX_RENDER_ATTEMPTS retries so a browser that is genuinely and
 *   persistently blocked — e.g. CDP-instrumented automation, which Turnstile is
 *   designed to flag — gives up rather than looping forever; the pre-fix fail-closed
 *   403 behavior is the ceiling, never made worse).
 *
 * FAST-FOLLOW (2026-07-26, same outage window): the first version above cleared only
 * the JS-side singleton, not the DOM-level widget — Cloudflare's own render() guard
 * then rejected every retry ("Turnstile has already been rendered in this container"),
 * confirmed live on production via Chrome DevTools console. error-callback now calls
 * turnstile.remove() on the old widget id (guarded) before clearing the singleton and
 * retrying — verified via console observation that the reject message no longer
 * appears and a fresh widget re-renders. See the inline comment at the render() call.
 *
 * RETRY-CADENCE FIX (2026-07-29 — ae-live-outcome re-fire, real submissions failing
 * again after the above fixes were live): scripts/e2e-form-submit.mjs against staging,
 * BOTH headless and headed (headed is the mode that reliably solved a real challenge
 * pre-07-26 in ~15-20s), reproduced 100% failure with a new console signature —
 * `[Cloudflare Turnstile] Nothing to reset found for provided container.` firing 3x
 * in under 2 seconds, then nothing for the rest of the ~38s run. Cloudflare's widget
 * legitimately throws transient errors and calls error-callback more than once while
 * it self-heals internally over that same 15-20s window (this is documented behavior
 * in e2e-form-submit.mjs's own waitForTurnstileToken() comment, confirmed by the
 * pre-07-26 baseline actually solving on that timescale with NO error-callback at
 * all). The 07-26 fix reacted to every error-callback firing as if the widget were
 * permanently dead: at RETRY_DELAY_MS=400ms with no guard against re-entrant firings,
 * a normal burst of 2-3 transient errors (Cloudflare's own self-heal in progress)
 * exhausted all MAX_RENDER_ATTEMPTS and called turnstile.remove() on each one —
 * tearing the widget down mid-solve instead of leaving it to recover on its own,
 * turning a self-recovering condition into a guaranteed permanent failure for real
 * visitors. Fix: `retryScheduled` ignores re-entrant error-callback firings while a
 * retry/rebuild is already pending, and RETRY_DELAY_MS is raised from 400ms to 8s so
 * a freshly rendered widget gets a realistic shot at solving (up to ~24s across all
 * attempts) before we intervene again, instead of a rebuild every fraction of a
 * second. Re-verified on staging via scripts/e2e-form-submit.mjs after this change.
 *
 * Governance: SOP-WEBSITE-03, SOP-BRAND-01
 */

import { useEffect, useRef } from 'react';

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? '';
const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';

interface UseTurnstileReturn {
  /** Attach to a container div. The widget will render inside it off-screen. */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /** Call on form submit to get the current solved token. Returns '' if not provisioned. */
  getToken: () => string;
  /** Call after a successful submit to reset the widget for the next submission. */
  resetWidget: () => void;
}

// Module-level singleton — see "SINGLETON WIDGET FIX" note above. Shared across every
// useTurnstile() instance on the page so only one Turnstile widget ever renders at once.
let sharedWidgetId: string | null = null;
let sharedContainer: HTMLElement | null = null;

// ERROR-CALLBACK / RETRY FIX (2026-07-26) — bounds how many times a single page load
// will re-render a widget after an error-callback fires. Resets per page load (module
// re-evaluates on full navigation); intentionally NOT reset on unmount/remount within
// the same page so a form that keeps erroring doesn't retry forever across re-mounts.
let sharedRenderAttempts = 0;
const MAX_RENDER_ATTEMPTS = 3;
// RETRY-CADENCE FIX (2026-07-29): was 400ms — too fast to tell a genuinely dead
// widget apart from Cloudflare's own normal multi-attempt self-heal (which can take
// 15-20s). See the file-header comment for the outage this caused.
const RETRY_DELAY_MS = 8000;
// RETRY-CADENCE FIX (2026-07-29): Cloudflare can call error-callback more than once
// in quick succession while it self-heals internally. Without this guard, each of
// those normal firings consumed one of MAX_RENDER_ATTEMPTS and tore the widget down
// via turnstile.remove(), exhausting the budget before the widget ever got a real
// chance to solve. Ignore re-entrant firings while a retry is already pending.
let retryScheduled = false;

export function useTurnstile(): UseTurnstileReturn {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isOwnerRef = useRef(false);

  // Inject the Turnstile script once per page (idempotent across multiple hook instances).
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (document.getElementById(TURNSTILE_SCRIPT_ID)) return;
    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    // data-explicit-render: prevents auto-scan so we control when render() is called.
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  // Render the widget once the container div is in the DOM — but only if no other
  // instance on this page already owns a live widget (singleton guard).
  //
  // MOUNT-ONCE FIX (2026-08-07 — ae-live-outcome re-fire, contact-form Turnstile
  // never solving): this effect previously had no dependency array, so it re-ran
  // on EVERY re-render of any consuming component, not just on mount —
  // contradicting its own "render the widget once" comment above and its two
  // sibling effects in this hook (both `[]`). /contact mounts TWO simultaneous
  // useTurnstile() consumers (ContactFormWidget + the page-level NewsletterForm —
  // the same multi-instance shape as the 2026-07-16 blog-page bug this hook's
  // singleton was built to handle). ContactFormWidget re-renders mid-submit
  // (setSubmitting(true)) at almost exactly the moment a transient Cloudflare
  // error-callback can leave sharedWidgetId briefly null — the un-gated effect
  // re-running at that instant reopened the singleton race (a second concurrent
  // render() into the same container, racing the error-callback's own pending 8s
  // retry), reproducing the observed `[Cloudflare Turnstile] Nothing to reset
  // found for provided container.` errors on 2/2 headed Playwright runs against
  // staging before this fix. Added the missing `[]` — TURNSTILE_SITE_KEY is a
  // module constant and containerRef is a stable ref, so no reactive dependency
  // is lost by pinning this to mount-only.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;

    // Another instance already rendered a widget and its container is still in the DOM —
    // this instance is a follower, not an owner. Do not render a second widget.
    if (sharedWidgetId !== null && sharedContainer && document.contains(sharedContainer)) {
      return;
    }

    const el = containerRef.current;
    if (!el) return;

    function tryRender() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const turnstile = (window as any).turnstile;
      if (turnstile && typeof turnstile.render === 'function' && el && sharedWidgetId === null) {
        sharedWidgetId = turnstile.render(el, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: 'light',
          // ERROR-CALLBACK / RETRY FIX (2026-07-26): without this, a client-side
          // Turnstile error (e.g. 600010) left the widget dead for the rest of the
          // page load — getToken() returned '' forever and every submit 403'd with
          // no way for the visitor to recover short of a full reload. Now: clear the
          // singleton and re-render a fresh widget, bounded by MAX_RENDER_ATTEMPTS so
          // a browser that is genuinely and persistently blocked doesn't loop forever.
          //
          // FAST-FOLLOW (2026-07-26, same outage window): the first version of this
          // fix cleared the JS-side singleton but never removed the DOM-level widget
          // from the container, so Cloudflare's render() guard rejected every retry
          // with "Turnstile has already been rendered in this container." — verified
          // live on production via Chrome DevTools console. Fix: call
          // turnstile.remove(idToRemove) on the OLD widget id first (guarded — the
          // widget may already be gone/errored out from Cloudflare's own side), THEN
          // clear the singleton and retry. This is the step that actually frees the
          // container so re-render can succeed.
          'error-callback': () => {
            // RETRY-CADENCE FIX (2026-07-29): Cloudflare fires error-callback more than
            // once while it self-heals a transient error internally — ignore re-entrant
            // firings while a retry is already scheduled, so a normal burst of 2-3
            // transient errors doesn't instantly exhaust MAX_RENDER_ATTEMPTS.
            if (retryScheduled) return;
            const idToRemove = sharedWidgetId;
            try {
              if (idToRemove && turnstile && typeof turnstile.remove === 'function') {
                turnstile.remove(idToRemove);
              }
            } catch {
              // Widget may already be gone on Cloudflare's side — non-fatal.
            }
            sharedWidgetId = null;
            sharedContainer = null;
            sharedRenderAttempts += 1;
            if (sharedRenderAttempts < MAX_RENDER_ATTEMPTS) {
              retryScheduled = true;
              setTimeout(() => {
                retryScheduled = false;
                if (sharedWidgetId === null && el && document.contains(el)) {
                  tryRender();
                }
              }, RETRY_DELAY_MS);
            }
            // else: give up — getToken() keeps returning '' and the server's existing
            // fail-closed 403 applies, same ceiling as before this fix.
          },
        }) as string;
        sharedContainer = el;
        isOwnerRef.current = true;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).turnstile) {
      tryRender();
      return;
    }

    // Poll until script loads (max ~3s, 10 × 300ms).
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).turnstile) {
        clearInterval(interval);
        tryRender();
      } else if (attempts >= 10) {
        clearInterval(interval); // give up — fail-open
      }
    }, 300);

    return () => clearInterval(interval);
  }, []);

  // If this instance owns the shared widget, clear the singleton on unmount so the
  // next page / remount renders a fresh widget instead of pointing at a dead container.
  useEffect(() => {
    return () => {
      if (isOwnerRef.current) {
        sharedWidgetId = null;
        sharedContainer = null;
      }
    };
  }, []);

  const getToken = (): string => {
    if (!TURNSTILE_SITE_KEY) return '';
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const turnstile = (window as any).turnstile;
      if (turnstile && typeof turnstile.getResponse === 'function' && sharedWidgetId) {
        return turnstile.getResponse(sharedWidgetId) ?? '';
      }
    } catch {
      // Fail-open
    }
    return '';
  };

  const resetWidget = (): void => {
    if (!TURNSTILE_SITE_KEY) return;
    try {
      const turnstile = (window as { turnstile?: { reset?: (id?: string) => void } }).turnstile;
      if (sharedWidgetId) {
        turnstile?.reset?.(sharedWidgetId);
      }
    } catch {
      // Non-fatal
    }
  };

  return { containerRef, getToken, resetWidget };
}
